import { useEffect, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useUser } from "@/hooks/useUser";
import { sendPhoneVerification, checkPhoneVerification, ApiError } from "@/lib/users-api";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { cn } from "@/lib/utils";
import { splitPhone } from "@/lib/countryDialCodes";
import { PhoneNumberInput, usePreferredDial } from "./PhoneNumberInput";
import { LiveStatus } from "@/components/ui/live-status";

/**
 * Log structured verify_sid_invalid details to the browser console for
 * support / on-call inspection without leaking them into the renter-facing
 * banner. The server already logs the same fields at error level, but
 * mirroring them client-side is convenient when debugging from a Zoom call
 * with a host.
 */
function logAdminVerifyDetails(err: ApiError): void {
  const d = err.details || {};
  // eslint-disable-next-line no-console
  console.warn("[phone-verify] verify_sid_invalid", {
    envVar: d.envVar,
    region: d.region,
    verifySidStatus: d.verifySidStatus,
    twilioCode: d.twilioCode,
  });
}

export interface PhoneVerificationProps {
  initialPhone?: string | null;
  onVerified?: (phone: string) => void;
  onPhoneChanged?: (phone: string) => void;
  onCancel?: () => void;
  cancelLabel?: string;
  autoFocus?: boolean;
  className?: string;
  variant?: "default" | "confirmation";
}

export function PhoneVerification({
  initialPhone,
  onVerified,
  onPhoneChanged,
  onCancel,
  cancelLabel,
  autoFocus,
  className,
  variant = "default",
}: PhoneVerificationProps) {
  const confirmation = variant === "confirmation";
  const { session } = useUser();
  const { t, i18n } = useAppTranslation();

  const preferredDial = usePreferredDial();
  const initial = splitPhone(initialPhone, preferredDial);
  const [dialCode, setDialCode] = useState(initial.dial);
  const [localNumber, setLocalNumber] = useState(initial.rest);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const verifyRowRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [callLoading, setCallLoading] = useState(false);
  const [callSent, setCallSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Once the OTP is sent, scroll the code input + Verify button into view so
  // the on-screen keyboard (which opens for the auto-focused field) doesn't
  // cover the primary CTA on small phones.
  useEffect(() => {
    if (otpSent) {
      verifyRowRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [otpSent]);
  const [success, setSuccess] = useState(false);

  const fullPhone = `${dialCode}${localNumber.replace(/\D/g, "")}`;
  const e164Valid = /^\+\d{7,15}$/.test(fullPhone);
  const initialFullPhone = `${initial.dial}${initial.rest}`;
  const sessionReady = !!session?.access_token;

  async function handleSend() {
    if (!session?.access_token || !e164Valid) return;
    setLoading(true);
    setError(null);
    setCallSent(false);
    try {
      const res = await sendPhoneVerification(session.access_token, fullPhone);
      if (fullPhone !== initialFullPhone) {
        // Backend has now persisted the new phone and reset phone_verified=false.
        onPhoneChanged?.(fullPhone);
      }
      if (res.demo) {
        setSuccess(true);
        onVerified?.(fullPhone);
      } else {
        setOtpSent(true);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === "verify_sid_invalid") {
        // Renter-facing: surface only the friendly server message. The
        // structured `details` (envVar, region, verifySidStatus) used to
        // be concatenated into the banner for admin debugging, but a
        // renter clicking "Send code" on /uk/checkout would see
        // `TWILIO_US_VERIFY_SERVICE_SID for region US is not set` —
        // confusing at best, embarrassing at worst. The details are
        // still on `err.details` for devtools/log inspection and the
        // server logs the same fields at error level.
        setError(err.message);
        logAdminVerifyDetails(err);
      } else {
        setError(err instanceof Error ? err.message : t("verification.send_failed"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleVoiceCall() {
    if (!session?.access_token || !e164Valid) return;
    setCallLoading(true);
    setError(null);
    setCallSent(false);
    try {
      await sendPhoneVerification(session.access_token, fullPhone, {
        channel: "call",
        locale: i18n.language,
      });
      setCallSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === "verify_sid_invalid") {
        setError(err.message);
        logAdminVerifyDetails(err);
      } else {
        setError(err instanceof Error ? err.message : t("verification.call_failed"));
      }
    } finally {
      setCallLoading(false);
    }
  }

  async function handleCheck() {
    if (!session?.access_token || otp.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      await checkPhoneVerification(session.access_token, fullPhone, otp);
      setSuccess(true);
      onVerified?.(fullPhone);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("verification.invalid_code"));
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className={cn("flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700", className)}>
        <CheckCircle2 className="h-4 w-4" />
        <span>{t("verification.phone_verified_msg", { phone: fullPhone })}</span>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {!otpSent ? (
        <>
          <PhoneNumberInput
            dialCode={dialCode}
            onDialCodeChange={setDialCode}
            localNumber={localNumber}
            onLocalNumberChange={setLocalNumber}
            autoFocus={autoFocus}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSend}
              disabled={loading || !e164Valid || !sessionReady}
              className={confirmation
                ? "inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                : "inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground disabled:opacity-50"}
            >
              {loading && <Spinner size="sm" />}
              {loading ? t("verification.sending") : t("verification.send_code")}
            </button>
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="cursor-pointer rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/50"
              >
                {cancelLabel || t("common.cancel")}
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t("verification.enter_code", { phone: fullPhone })}</p>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="123456"
            aria-label={t("verification.otp_input_label", { defaultValue: "Verification code" })}
            aria-invalid={!!error}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-base md:text-sm tracking-widest"
            maxLength={6}
          />
          <div ref={verifyRowRef} className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleCheck}
              disabled={loading || otp.length !== 6}
              className={cn(
                "inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-50",
                otp.length === 6
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-accent text-accent-foreground",
              )}
            >
              {loading && <Spinner size="sm" />}
              {loading ? t("verification.verifying") : t("auth.verify")}
            </button>
            <button
              type="button"
              onClick={() => { setOtpSent(false); setOtp(""); setError(null); }}
              disabled={loading}
              className="rounded-md border border-border px-3 py-2 text-sm hover:bg-muted/50"
            >
              {t("verification.change_number")}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={loading || callLoading}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {t("verification.resend_code")}
            </button>
            <button
              type="button"
              onClick={handleVoiceCall}
              disabled={loading || callLoading}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {callLoading && <Spinner size="sm" />}
              {callLoading ? t("verification.calling_now") : t("verification.call_me_instead")}
            </button>
          </div>
          {callSent && !error && (
            <p className="text-xs text-muted-foreground">
              {t("verification.voice_call_sent", { phone: fullPhone })}
            </p>
          )}
        </>
      )}
      {error && (
        <LiveStatus politeness="assertive" className="text-xs text-destructive">
          {error}
        </LiveStatus>
      )}
    </div>
  );
}
